import { StyledCreditsLimitBanner } from '@common/credits/ProfessionalEdition/CreditsLimitBanner';
import { useProfessionalCreditStatus } from '@common/credits/ProfessionalEdition/hooks';
import { useDeleteProject } from '@common/projectHooks/useDeleteProject';
import { deleteEntityQuery, QueryKeys } from '@common/queries/common';
import { ProjectSourceType } from '@common/types';
import { Entity } from '@common/types/Entity';
import { VAPP_Private_Routes } from '@common/url';
import { useIsVappImportProjectEnabled, useUserWorkspace } from '@common/VerticalApps/hooks';
import { useVerticalAppsRecentActivitySync } from '@common/VerticalApps/recentActivity/useVerticalAppsRecentActivity';
import { useReconcileVerticalAppsProjectPreferences } from '@common/VerticalApps/useReconcileVerticalAppsProjectPreferences';
import { useVerticalAppsProjectList } from '@common/VerticalApps/useVerticalAppsProjectList';
import { useVerticalAppsSideNavProjectsPreference } from '@common/VerticalApps/useVerticalAppsSideNavProjectsPreference';
import { withGuard } from '@common/withGuard';
import { EntityKind } from '@prophecy/graphql-types/enums';
import { PlusIcon, SearchSmIcon, DownloadDIcon } from '@prophecy/icons';
import { theme } from '@prophecy/theme';
import { Button, Container, Dialog, Display, Input, Spinner, Stack, Text, toast } from '@prophecy/ui-v3';
import { InputSize } from '@prophecy/ui-v3/Input/types';
import { usePersistentCallback } from '@prophecy/utils/react/hooks';
import { useQueryClient } from '@tanstack/react-query';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';

import { useGraphQlMutation } from '../../../data/util';
import {
  computeProjectListingFacets,
  EMPTY_PROJECT_LISTING_FILTERS,
  filterProjectsByListingFilters,
  type ProjectListingFilters
} from './entityFilterUtils';
import { ProjectCard } from './ProjectCard';
import { ProjectListingFilter } from './ProjectListingFilter';

const TABS = [
  { key: 'all', label: 'All' },
  { key: 'yours', label: 'Your projects' }
] as const;

type TabKey = (typeof TABS)[number]['key'];

const StyledContainer = styled(Container)`
  padding-top: ${theme.spaces.x80};
  overflow: initial;
  margin: 0 auto;
`;

const StyledTabBar = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: ${theme.spaces.x12};
`;

const StyledHeaderRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: ${theme.spaces.x12};
`;

const StyledTabGroup = styled.div`
  display: flex;
  align-items: center;
  gap: ${theme.spaces.x4};
  background: ${theme.colors.gray100};
  border-radius: ${theme.radius.x8};
  padding: 3px;
`;

const StyledTab = styled.button<{ $active: boolean }>`
  all: unset;
  cursor: pointer;
  padding: ${theme.spaces.x4} ${theme.spaces.x12};
  border-radius: ${theme.radius.x6};
  font-size: 14px;
  font-weight: ${(p) => (p.$active ? theme.fontWeight.semiBold : theme.fontWeight.normal)};
  color: ${(p) => (p.$active ? theme.colors.gray900 : theme.colors.gray600)};
  background: ${(p) => (p.$active ? theme.colors.white : 'transparent')};
  box-shadow: ${(p) => (p.$active ? theme.shadows.sm : 'none')};
  transition:
    background 0.15s ease,
    color 0.15s ease,
    box-shadow 0.15s ease;

  &:hover {
    color: ${theme.colors.gray900};
  }
`;

const StyledSearchRow = styled.div`
  display: flex;
  align-items: center;
  gap: ${theme.spaces.x8};
`;

const StyledSearchInput = styled.div`
  width: 220px;
`;

const StyledProjectGrid = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: ${theme.spaces.x16};
  padding: ${theme.spaces.x16} 0;
`;

const PROJECT_LIST_STATE_HEIGHT = '200px';

export default function ProjectListing() {
  const navigate = useNavigate();
  const { isProjectPinned, togglePinnedProject, removeSideNavProject } = useVerticalAppsSideNavProjectsPreference();
  const { removeRecentActivityForProject } = useVerticalAppsRecentActivitySync();
  const { currentWorkspace } = useUserWorkspace();

  const [activeTab, setActiveTab] = useState<TabKey>('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [entityFilters, setEntityFilters] = useState<ProjectListingFilters>(EMPTY_PROJECT_LISTING_FILTERS);

  const { projects, isLoading, uid } = useVerticalAppsProjectList();

  // Workspace + tab + search narrowing. Entity-kind facet counts are derived from this set.
  const searchedProjects = useMemo(() => {
    const inWorkspace = projects.filter((project) => project.team?._id === currentWorkspace?._id);
    const byTab = activeTab === 'yours' ? inWorkspace.filter((project) => project.createdBy === uid) : inWorkspace;
    const query = searchQuery.trim().toLowerCase();
    // Match the project name OR any contained entity's name (e.g. a CDM, data mapping, pipeline or
    // analysis), so users can find "all projects containing CDM X" — the entity names are already in
    // the project list payload, so no extra fetch is needed.
    return query
      ? byTab.filter(
          (project) =>
            project.name.toLowerCase().includes(query) ||
            (project.entities ?? []).some((entity) =>
              (entity.items ?? []).some((item) => item.name?.toLowerCase().includes(query))
            )
        )
      : byTab;
  }, [projects, currentWorkspace?._id, activeTab, uid, searchQuery]);

  const entityFacets = useMemo(() => computeProjectListingFacets(searchedProjects), [searchedProjects]);

  const filteredProjects = useMemo(
    () => filterProjectsByListingFilters(searchedProjects, entityFilters),
    [searchedProjects, entityFilters]
  );

  // This page loads the authoritative project list, so reconcile stale preference entries here.
  useReconcileVerticalAppsProjectPreferences();

  const queryClient = useQueryClient();
  const deleteProjectMutation = useGraphQlMutation(deleteEntityQuery);
  const deleteProject = useDeleteProject(deleteProjectMutation);

  const isVappImportProjectEnabled = useIsVappImportProjectEnabled();
  const { isBlocked, blockedReason } = useProfessionalCreditStatus();

  useEffect(() => {
    setEntityFilters(EMPTY_PROJECT_LISTING_FILTERS);
  }, [activeTab, currentWorkspace?._id]);

  const handlePinProject = useCallback(
    (projectId: string) => {
      const project = projects.find((projectItem) => projectItem._id === projectId);
      if (!project) return;

      togglePinnedProject({
        projectId: project._id,
        name: project.name,
        workspaceId: project.team?._id
      });
    },
    [projects, togglePinnedProject]
  );

  const handleDeleteProject = useCallback(
    async (projectId: string, projectName: string) => {
      const confirmation = await Dialog.alert({
        'data-test-id': 'vapp-delete-project-dialog',
        title: 'Delete project',
        subTitle: (
          <span>Are you sure you want to delete &ldquo;{projectName}&rdquo;? This action cannot be undone.</span>
        ),
        okButton: { children: 'Delete', variant: 'primary', danger: true }
      });
      if (confirmation !== Dialog.ResponseAction.okButton) return;

      const loadingToast = toast.loading({
        content: `Deleting ${projectName}`,
        'data-test-id': 'vapp-delete-project-toast'
      });
      try {
        await deleteProject.mutateAsync({ uid: projectId, entityKind: EntityKind.Project });
        loadingToast.update({ content: 'Project deleted', variant: 'success' });
        queryClient.invalidateQueries([QueryKeys.ProjectList]);
        removeSideNavProject(projectId);
        removeRecentActivityForProject(projectId);
      } catch {
        loadingToast.update({ content: 'Failed to delete project', variant: 'error' });
      }
    },
    [deleteProject, queryClient, removeSideNavProject, removeRecentActivityForProject]
  );

  const navigateToCreateProject = (isImportProject?: boolean) => {
    if (isImportProject) {
      navigate(VAPP_Private_Routes.Project_Create.getUrl(undefined, { projectType: ProjectSourceType.imported }));
    } else {
      navigate(VAPP_Private_Routes.Project_Create.getUrl(undefined));
    }
  };

  const handleCreateProject = usePersistentCallback(
    withGuard(() => navigateToCreateProject(false), {
      reason: blockedReason,
      blocked: isBlocked
    })
  );

  const handleImportProject = usePersistentCallback(
    withGuard(() => navigateToCreateProject(true), {
      reason: blockedReason,
      blocked: isBlocked
    })
  );

  const handleOpenEntity = useCallback(
    (projectId: string, entity: Entity, name: string) => {
      navigate(VAPP_Private_Routes.Project_Detail.getUrl({ uid: projectId }, { entity: entity.toLowerCase(), name }));
    },
    [navigate]
  );

  const handleOpenProject = usePersistentCallback((projectId: string) => {
    navigate(VAPP_Private_Routes.Project_Detail.getUrl({ uid: projectId }));
  });

  return (
    <Root>
      <StyledCreditsLimitBanner />
      <Stack grow='1' align='center' alignY='center'>
        <StyledContainer>
          <Stack gap={theme.spaces.x20}>
            <StyledHeaderRow>
              <Display level='xs' weight={theme.fontWeight.semiBold} tone={theme.colors.gray900}>
                All Projects
              </Display>
              <Stack direction='horizontal' gap={theme.spaces.x16}>
                {isVappImportProjectEnabled && (
                  <Button
                    variant='tertiary'
                    size='s'
                    onClick={handleImportProject}
                    iconPlacement='left'
                    icon={<DownloadDIcon type='default' />}
                    data-test-id='create-entity-import-project-button'>
                    Import
                  </Button>
                )}
                <Button
                  variant='secondaryGray'
                  size='s'
                  icon={<PlusIcon type='default' />}
                  iconPlacement='left'
                  onClick={handleCreateProject}
                  data-test-id='project-listing-create-project-button'>
                  Create New
                </Button>
              </Stack>
            </StyledHeaderRow>

            <StyledTabBar>
              <StyledTabGroup>
                {TABS.map(({ key, label }) => (
                  <StyledTab key={key} $active={activeTab === key} onClick={() => setActiveTab(key)}>
                    {label}
                  </StyledTab>
                ))}
              </StyledTabGroup>

              <StyledSearchRow>
                <StyledSearchInput>
                  <Input
                    placeholder='Search projects'
                    value={searchQuery}
                    onChange={(value) => setSearchQuery(value)}
                    icon={<SearchSmIcon type='default' />}
                    iconPlacement='left'
                    data-test-id='project-listing-search-input'
                    inputSize={InputSize.s}
                  />
                </StyledSearchInput>
                <ProjectListingFilter facets={entityFacets} filters={entityFilters} onChange={setEntityFilters} />
              </StyledSearchRow>
            </StyledTabBar>

            {isLoading ? (
              <Stack alignY='center' align='center' height={PROJECT_LIST_STATE_HEIGHT}>
                <Spinner />
              </Stack>
            ) : filteredProjects.length === 0 ? (
              <Stack alignY='center' align='center' height={PROJECT_LIST_STATE_HEIGHT}>
                <Text level='sm' tone={theme.colors.gray500}>
                  No projects found
                </Text>
              </Stack>
            ) : (
              <StyledProjectGrid>
                {filteredProjects.map((project) => (
                  <ProjectCard
                    key={project._id}
                    project={project}
                    onClick={handleOpenProject}
                    onOpenEntity={handleOpenEntity}
                    isPinned={isProjectPinned(project._id)}
                    onPinProject={handlePinProject}
                    onDeleteProject={handleDeleteProject}
                  />
                ))}
              </StyledProjectGrid>
            )}
          </Stack>
        </StyledContainer>
      </Stack>
    </Root>
  );
}

const Root = styled(Stack)`
  overflow-y: auto;
  height: 100%;
`;
